import { CheckIn } from '../types';

export const STREAK_MILESTONES = [4, 8, 12, 26, 52, 104];
export const FREEZE_RECHARGE_WEEKS = 4;
export const STREAK_GRID_WEEKS = 12;

export type WeekStatus = 'checked' | 'frozen' | 'missed' | 'pending' | 'none';

export interface StreakSummary {
  current: number;
  longest: number;
  checkedInThisWeek: boolean;
  freezeAvailable: boolean;
  /** Oldest to newest, ending with the current week. */
  grid: WeekStatus[];
  nextMilestone: number | null;
}

/** Local calendar date as YYYY-MM-DD. */
export const toDateKey = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

/** Monday-based week number. Day 0 (1970-01-01) was a Thursday, hence the +3. */
export const weekIndexOf = (dateKey: string): number => {
  const [y, m, d] = dateKey.split('-').map(Number);
  const day = Date.UTC(y, m - 1, d) / 86_400_000;
  return Math.floor((day + 3) / 7);
};

export const summarizeStreak = (checkIns: CheckIn[], today: Date = new Date()): StreakSummary => {
  const currentWeek = weekIndexOf(toDateKey(today));
  const checkedWeeks = new Set(checkIns.map(c => weekIndexOf(c.date)).filter(w => w <= currentWeek));
  const firstWeek = checkedWeeks.size ? Math.min(...Array.from(checkedWeeks)) : currentWeek;

  const statuses = new Map<number, WeekStatus>();
  let streak = 0;
  let longest = 0;
  let freezeAvailable = true;
  let weeksSinceFreezeUsed = 0;

  for (let week = firstWeek; week <= currentWeek; week++) {
    if (checkedWeeks.has(week)) {
      streak++;
      statuses.set(week, 'checked');
      if (!freezeAvailable && ++weeksSinceFreezeUsed >= FREEZE_RECHARGE_WEEKS) {
        freezeAvailable = true;
      }
    } else if (week === currentWeek) {
      statuses.set(week, 'pending');
    } else if (streak > 0 && freezeAvailable) {
      freezeAvailable = false;
      weeksSinceFreezeUsed = 0;
      statuses.set(week, 'frozen');
    } else {
      streak = 0;
      freezeAvailable = true;
      statuses.set(week, 'missed');
    }
    longest = Math.max(longest, streak);
  }

  const grid: WeekStatus[] = [];
  for (let week = currentWeek - STREAK_GRID_WEEKS + 1; week <= currentWeek; week++) {
    grid.push(statuses.get(week) ?? (week === currentWeek ? 'pending' : 'none'));
  }

  return {
    current: streak,
    longest,
    checkedInThisWeek: checkedWeeks.has(currentWeek),
    freezeAvailable,
    grid,
    nextMilestone: STREAK_MILESTONES.find(m => m > streak) ?? null,
  };
};
